import { api, categoryColor, type Folder, type MessageSummary } from "../api";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCheck,
  Flag,
  Mail,
  MailOpen,
  Menu,
  OctagonAlert,
  Paperclip,
  RefreshCw,
  Search,
  Trash2,
  UserRound,
  X,
  Wand2,
  MessagesSquare,
} from "lucide-react";
import { KontextMenue, type MenuEintrag } from "./KontextMenue";
import { OrdnerAktionen } from "./OrdnerAktionen";
import { Select } from "./Select";
import { datumKurz, datumLang } from "../lib/datum";
import { ordnerOptionen } from "../lib/ordner";
import { toast } from "./ui/Toast";
import { WischZeile, useZiehenAktualisieren } from "./Gesten";
import { useSchmal } from "../lib/mobil";
import { Avatar } from "./Avatar";
import { bestaetigen } from "./ui/Bestaetigen";

interface Props {
  messages: MessageSummary[];
  selectedId: number | null;
  loading: boolean;
  query: string;
  title: string;
  onQuery: (q: string) => void;
  onSelect: (id: number) => void;
  onRefresh: () => void;
  onOpenNav: () => void;
  active: boolean;
  unreadOnly: boolean;
  onUnreadOnly: (value: boolean) => void;
  folders: Folder[];
  onBulkDone: () => void;
  /** Gelesen/ungelesen sofort zeigen, bevor der Server antwortet. */
  onGelesenLokal?: (ids: number[], gelesen: boolean) => void;
  /** Braucht keine Antwort — verlässt „Zu beantworten". */
  onErledigt?: (ids: number[]) => void;
  absender: string | null;
  onAbsender: (a: string | null) => void;
  onReply: (messageId: number, kind: "antwort" | "allen" | "weiterleiten") => void;
  /** Nur in Unerwünscht und Papierkorb gesetzt: öffnet „Ordner leeren". */
  onLeeren?: () => void;
  /** Der gerade gezeigte Ordner — für das Aktionsmenü im Kopf. */
  ordner?: Folder;
  onFoldersChanged?: () => void;
  onOrdnerGeloescht?: () => void;
  /** Eine Zeile pro Unterhaltung statt jeder einzelnen Mail. */
  unterhaltungen?: boolean;
  onUnterhaltungen?: (an: boolean) => void;
  /** Regeln und Spam-Prüfung auf diese Ansicht neu anwenden. */
  onNeuEinsortieren?: () => void;
  /** Gibt es ältere Mails, als die Liste gerade zeigt? */
  mehrDa?: boolean;
  onMehr?: () => void;
  /** Fehlertext, wenn das Laden der Liste scheiterte. */
  fehler?: string | null;
}

type Massenaktion = "move" | "trash" | "markRead" | "markUnread" | "flag" | "unflag" | "spam";

const ERFOLG: Record<Massenaktion, (n: number) => string> = {
  move: (n) => `${n === 1 ? "Nachricht" : `${n} Nachrichten`} verschoben`,
  trash: (n) => `${n === 1 ? "Nachricht" : `${n} Nachrichten`} in den Papierkorb verschoben`,
  markRead: (n) => `${n === 1 ? "Als" : `${n} als`} gelesen markiert`,
  markUnread: (n) => `${n === 1 ? "Als" : `${n} als`} ungelesen markiert`,
  flag: (n) => `${n === 1 ? "Als" : `${n} als`} wichtig markiert`,
  unflag: (n) => `Markierung bei ${n === 1 ? "einer Nachricht" : `${n} Nachrichten`} entfernt`,
  spam: (n) => `${n === 1 ? "Als" : `${n} als`} unerwünscht eingestuft`,
};

/** Der Server nimmt höchstens 500 Kennungen je Aufruf. */
async function inBloecken(ids: number[], action: Massenaktion, folder?: string) {
  let erledigt = 0;
  const fehler: string[] = [];
  for (let i = 0; i < ids.length; i += 500) {
    const r = await api.bulkAction(ids.slice(i, i + 500), action, folder);
    erledigt += r.erledigt;
    fehler.push(...r.fehler);
  }
  return { erledigt, fehler };
}

export function MessageList({ messages, selectedId, loading, query, title, onQuery, onSelect, onRefresh, onOpenNav, active, unreadOnly, onUnreadOnly, folders, onBulkDone, onGelesenLokal, onErledigt, absender, onAbsender, onReply, onLeeren, ordner, onFoldersChanged, onOrdnerGeloescht, onNeuEinsortieren, unterhaltungen = false, onUnterhaltungen, mehrDa, onMehr, fehler }: Props) {
  const [absenderListe, setAbsenderListe] = useState<{ email: string; name: string; anzahl: number }[] | null>(null);
  const [absenderOffen, setAbsenderOffen] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [anchor, setAnchor] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [menue, setMenue] = useState<{ x: number; y: number; id: number } | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  const schmal = useSchmal();
  const ziehen = useZiehenAktualisieren(scroller, onRefresh, schmal && active);
  // Auf dem Telefon: Sobald etwas gewählt ist, wählt Antippen statt zu öffnen.
  const auswahlModus = schmal && picked.size > 0;

  // Auswahl auf das beschränken, was die Liste gerade zeigt. Sonst wirkt eine
  // Massenaktion nach dem Ordnerwechsel auf unsichtbare Mails.
  useEffect(() => {
    setPicked((prev) => {
      if (prev.size === 0) return prev;
      const da = new Set(messages.map((m) => m.id));
      const next = new Set([...prev].filter((id) => da.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [messages]);

  // Die gewählte Zeile in Sicht halten — wichtig beim Blättern mit J und K.
  useEffect(() => {
    if (selectedId === null) return;
    scroller.current
      ?.querySelector<HTMLElement>(`[data-id="${selectedId}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  const toggle = (id: number, shift: boolean) => {
    setPicked((prev) => {
      const next = new Set(prev);
      // Umschalt-Klick wählt den Bereich seit dem letzten Klick — so kennt man
      // es aus jedem Mailprogramm.
      if (shift && anchor !== null) {
        const from = messages.findIndex((m) => m.id === anchor);
        const to = messages.findIndex((m) => m.id === id);
        if (from >= 0 && to >= 0) {
          for (let i = Math.min(from, to); i <= Math.max(from, to); i++) {
            const m = messages[i];
            if (m) next.add(m.id);
          }
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setAnchor(id);
  };

  const ausfuehren = async (ids: number[], action: Massenaktion, folder?: string) => {
    if (ids.length === 0) return;
    setBusy(true);
    if (action === "markRead" || action === "markUnread") onGelesenLokal?.(ids, action === "markRead");
    try {
      const r = await inBloecken(ids, action, folder);
      if (r.fehler.length > 0) {
        toast.fehler(
          r.erledigt > 0
            ? `${r.erledigt} erledigt, ${r.fehler.length} fehlgeschlagen: ${r.fehler[0]}`
            : r.fehler[0] ?? "Aktion fehlgeschlagen",
        );
      } else {
        toast.erfolg(ERFOLG[action](r.erledigt || ids.length));
      }
      setPicked(new Set());
      setAnchor(null);
      onBulkDone();
    } catch (err) {
      toast.fehler(err);
    } finally {
      setBusy(false);
    }
  };

  const allPicked = messages.length > 0 && picked.size === messages.length;

  // Verschieben nur in Ordner des Kontos der Auswahl — bei gemischter Auswahl alle.
  const konten = useMemo(() => {
    const ids = new Set<number>();
    for (const m of messages) if (picked.has(m.id)) ids.add(m.account_id);
    return ids;
  }, [messages, picked]);
  const zielOrdner = ordnerOptionen(folders, konten.size === 1 ? [...konten][0] : null);

  /*
   * Rechtsklick auf eine ausgewählte Zeile wirkt auf die ganze Auswahl, sonst
   * nur auf die angeklickte. Das ist das Verhalten, das man aus Dateimanagern
   * und Mailprogrammen kennt.
   */
  const zielIds = (id: number): number[] => (picked.has(id) && picked.size > 1 ? [...picked] : [id]);

  const menueFuer = (id: number): MenuEintrag[] => {
    const ids = zielIds(id);
    const mehrere = ids.length > 1;
    const nachricht = messages.find((m) => m.id === id);
    const ungelesen = nachricht?.seen === 0;

    return [
      { id: "oeffnen", label: mehrere ? `${ids.length} ausgewählt` : "Öffnen",
        aktion: mehrere ? undefined : () => onSelect(id) },

      ...(mehrere
        ? []
        : [
            { id: "antwort", label: "Antworten", trenner: true, aktion: () => onReply(id, "antwort") },
            { id: "allen", label: "Allen antworten", aktion: () => onReply(id, "allen") },
            { id: "weiter", label: "Weiterleiten", aktion: () => onReply(id, "weiterleiten") },
          ]),

      ...(onErledigt && (mehrere || nachricht?.needs_reply === 1 || nachricht?.draft_id)
        ? [{ id: "erledigt", label: mehrere ? `${ids.length} als erledigt markieren` : "Erledigt — keine Antwort nötig",
             trenner: mehrere, aktion: () => { onErledigt(ids); setPicked(new Set()); } }]
        : []),

      {
        id: "gelesen",
        label: ungelesen || mehrere ? "Als gelesen markieren" : "Als ungelesen markieren",
        trenner: true,
        aktion: () => ausfuehren(ids, ungelesen || mehrere ? "markRead" : "markUnread"),
      },
      {
        id: "wichtig",
        label: nachricht?.flagged === 1 && !mehrere ? "Markierung entfernen" : "Als wichtig markieren",
        aktion: () => ausfuehren(ids, nachricht?.flagged === 1 && !mehrere ? "unflag" : "flag"),
      },

      {
        id: "verschieben",
        label: "Verschieben nach",
        trenner: true,
        unterliste: ordnerOptionen(folders, mehrere ? null : nachricht?.account_id, nachricht && !mehrere ? nachricht.folder_id : undefined)
          .map((o) => ({ id: o.value, label: o.label })),
      },
      { id: "spam", label: "Als unerwünscht einstufen", aktion: () => ausfuehren(ids, "spam") },
      {
        id: "papierkorb",
        label: mehrere ? `${ids.length} in den Papierkorb` : "In den Papierkorb",
        gefahr: true,
        aktion: () => ausfuehren(ids, "trash"),
      },
    ];
  };

  const absenderUmschalten = async () => {
    if (absender) return onAbsender(null);
    const auf = !absenderOffen;
    setAbsenderOffen(auf);
    if (auf && absenderListe === null) {
      setAbsenderListe(await api.absender(query).catch(() => []));
    }
  };

  const gefiltert = Boolean(query || unreadOnly || absender);

  return (
    <section className="pane list" data-active={active} aria-label={`Nachrichten: ${title}`}>
      <header className="pane-head list-kopf">
        <div className="list-titelzeile">
          <button type="button" className="icon-btn only-narrow" onClick={onOpenNav} aria-label="Navigation öffnen" title="Navigation">
            <Menu aria-hidden="true" />
          </button>
          <h2 className="list-titel" title={title}>{title}</h2>
          {messages.length > 0 && (
            <span className="list-anzahl" aria-label={`${messages.length}${mehrDa ? " oder mehr" : ""} Nachrichten`}>
              {messages.length}{mehrDa ? "+" : ""}
            </span>
          )}
          <div className="spacer" />
          <button
            type="button"
            className="icon-btn"
            aria-pressed={Boolean(absender) || absenderOffen}
            aria-expanded={absenderOffen && !absender}
            title={absender ? "Absenderfilter aufheben" : "Nach Absender filtern"}
            aria-label="Nach Absender filtern"
            onClick={() => void absenderUmschalten()}
          >
            <UserRound aria-hidden="true" />
          </button>
          {ordner && onFoldersChanged && (
            <OrdnerAktionen folder={ordner} folders={folders} variant="button" onChanged={onFoldersChanged} onDeleted={onOrdnerGeloescht} />
          )}
          {onNeuEinsortieren && (
            <button type="button" className="icon-btn" onClick={onNeuEinsortieren}
              title="Neu einsortieren — Regeln und Spam-Prüfung auf diese Ansicht anwenden (mit Vorschau)"
              aria-label="Neu einsortieren">
              <Wand2 aria-hidden="true" />
            </button>
          )}
          {onLeeren && (
            <button type="button" className="icon-btn" onClick={onLeeren} title="Ordner nach Prüfung endgültig leeren" aria-label="Ordner leeren">
              <Trash2 aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="icon-btn nur-breit"
            onClick={onRefresh}
            title="Neu laden"
            aria-label="Neu laden"
            data-dreht={loading || undefined}
          >
            <RefreshCw aria-hidden="true" />
          </button>
        </div>

        <div className="list-suche">
          <Search aria-hidden="true" />
          <input
            className="search"
            type="search"
            value={query}
            placeholder={`${title} durchsuchen`}
            onChange={(e) => onQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && query) { e.preventDefault(); e.stopPropagation(); onQuery(""); }
              else if (e.key === "Escape") (e.target as HTMLInputElement).blur();
              else if (e.key === "ArrowDown" && messages[0]) { e.preventDefault(); onSelect(messages[0].id); (e.target as HTMLInputElement).blur(); }
            }}
            aria-label="Nachrichten durchsuchen"
            title="Suchen (/)"
          />
        </div>

        <div className="list-schalter" role="group" aria-label="Anzeige">
          <button type="button" className="schalter" aria-pressed={unreadOnly} onClick={() => onUnreadOnly(!unreadOnly)}
            title={unreadOnly ? "Wieder alle Nachrichten zeigen" : "Nur ungelesene Nachrichten zeigen"}>
            {unreadOnly ? <Mail aria-hidden="true" /> : <MailOpen aria-hidden="true" />} Nur ungelesen
          </button>
          {onUnterhaltungen && (
            <button type="button" className="schalter" aria-pressed={unterhaltungen} onClick={() => onUnterhaltungen(!unterhaltungen)}
              title={unterhaltungen ? "Jede Mail einzeln zeigen" : "Antworten zu einer Zeile pro Unterhaltung bündeln"}>
              <MessagesSquare aria-hidden="true" /> Unterhaltungen
            </button>
          )}
        </div>

        {absender && (
          <div className="filter-leiste">
            {absender && (
              <span className="filter-chip" title={absender}>
                Von: {absender}
                <button type="button" aria-label="Absenderfilter aufheben" onClick={() => onAbsender(null)}><X aria-hidden="true" /></button>
              </span>
            )}
          </div>
        )}
        {loading && messages.length > 0 && <div className="lade-balken" aria-hidden="true" />}
      </header>

      {absenderOffen && !absender && (
        <div className="absender-wahl">
          <p className="hint" style={{ margin: "0 0 8px" }}>Häufigste Absender — Klick filtert die Liste:</p>
          {absenderListe === null ? (
            <p className="hint">Lade …</p>
          ) : absenderListe.length === 0 ? (
            <p className="hint">Keine Absender gefunden.</p>
          ) : (
            <div className="folder-picker">
              {absenderListe.map((a) => (
                <button
                  type="button"
                  className="chip pick"
                  key={a.email}
                  title={a.email}
                  onClick={() => { onAbsender(a.email); setAbsenderOffen(false); }}
                >
                  {a.name || a.email} · {a.anzahl}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {picked.size > 0 && (
        <div className="bulk-bar" role="toolbar" aria-label="Auswahl bearbeiten">
          <span className="bulk-count">{picked.size} ausgewählt</span>
          <button
            type="button"
            className="btn quiet"
            onClick={() => setPicked(allPicked ? new Set() : new Set(messages.map((m) => m.id)))}
          >
            {allPicked ? "Keine" : "Alle"}
          </button>
          <span className="bulk-trenner" />
          <button type="button" className="btn" disabled={busy} onClick={() => ausfuehren([...picked], "markRead")} title="Als gelesen markieren">
            <CheckCheck aria-hidden="true" /> Gelesen
          </button>
          <button type="button" className="btn" disabled={busy} onClick={() => ausfuehren([...picked], "spam")} title="Als unerwünscht einstufen">
            <OctagonAlert aria-hidden="true" /> Unerwünscht
          </button>
          <Select
            variant="button"
            value=""
            placeholder="Verschieben …"
            disabled={busy}
            options={zielOrdner}
            onChange={(v) => v && void ausfuehren([...picked], "move", v)}
            aria-label="In Ordner verschieben"
          />
          <button
            type="button"
            className="btn danger"
            disabled={busy}
            onClick={async () => {
              const n = picked.size;
              const ok = n < 5 || await bestaetigen({
                titel: `${n} Nachrichten in den Papierkorb?`,
                text: "Sie lassen sich aus dem Papierkorb wieder zurückholen.",
                bestaetigen: "In den Papierkorb",
                gefahr: true,
              });
              if (ok) void ausfuehren([...picked], "trash");
            }}
          >
            <Trash2 aria-hidden="true" /> Papierkorb
          </button>
          <button
            type="button"
            className="icon-btn bulk-schliessen"
            onClick={() => { setPicked(new Set()); setAnchor(null); }}
            aria-label="Auswahl aufheben"
            title="Auswahl aufheben"
          >
            <X aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="pane-scroll" ref={scroller} data-auswahl={auswahlModus || undefined}>
        {schmal && (ziehen.weite > 0 || ziehen.laedt) && (
          <div className="ziehen-anzeige" style={{ height: ziehen.weite }} aria-hidden="true">
            <RefreshCw data-bereit={ziehen.weite >= 64 || ziehen.laedt} data-dreht={ziehen.laedt || undefined}
              style={{ transform: ziehen.laedt ? undefined : `rotate(${ziehen.weite * 3}deg)` }} />
          </div>
        )}
        {fehler && messages.length === 0 ? (
          <div className="empty" role="alert">
            <strong>Liste konnte nicht geladen werden</strong>
            <span>{fehler}</span>
            <button type="button" className="btn" style={{ marginTop: 10 }} onClick={onRefresh}>Erneut versuchen</button>
          </div>
        ) : loading && messages.length === 0 ? (
          <ListenSkelett />
        ) : messages.length === 0 ? (
          <div className="empty">
            <strong>{gefiltert ? "Keine Treffer" : "Nichts hier"}</strong>
            <span>
              {query
                ? `Keine Nachricht passt zu „${query}".`
                : unreadOnly && absender
                  ? "Keine ungelesenen Nachrichten von diesem Absender."
                  : unreadOnly
                    ? "Alles gelesen."
                    : absender
                      ? "Keine Nachrichten von diesem Absender."
                      : "Dieser Ordner ist leer."}
            </span>
            {gefiltert && (
              <button
                type="button"
                className="btn quiet"
                style={{ marginTop: 8 }}
                onClick={() => { onQuery(""); onUnreadOnly(false); onAbsender(null); }}
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
        ) : (
          <ul className="msg-list" aria-busy={loading}>
            {messages.map((m) => {
              const spam = (m.spam_score ?? 0) >= 0.5 || m.category === "spam";
              const absenderName = m.from_name || m.from_email || "(unbekannt)";
              return (
                <li key={m.id} className="msg-row-wrap" data-picked={picked.has(m.id)} data-id={m.id}>
                  <WischZeile
                    aktiv={schmal && !auswahlModus}
                    onLangDruck={() => toggle(m.id, false)}
                    rechts={{
                      label: m.seen === 0 ? "Gelesen" : "Ungelesen",
                      icon: m.seen === 0 ? MailOpen : Mail,
                      farbe: "var(--accent)",
                      aktion: () => ausfuehren([m.id], m.seen === 0 ? "markRead" : "markUnread"),
                    }}
                    links={{
                      label: "Papierkorb",
                      icon: Trash2,
                      farbe: "var(--danger)",
                      entfernt: true,
                      aktion: () => ausfuehren([m.id], "trash"),
                    }}
                  >
                  <label className="msg-pick" title="Auswählen (Umschalt-Klick wählt einen Bereich)">
                    <input
                      type="checkbox"
                      checked={picked.has(m.id)}
                      onChange={() => { /* über onClick, dort kennen wir die Umschalttaste */ }}
                      onClick={(e) => { e.stopPropagation(); toggle(m.id, e.shiftKey); }}
                      aria-label={`${m.subject || "Nachricht"} von ${absenderName} auswählen`}
                    />
                  </label>
                  <button
                    type="button"
                    className="msg"
                    onContextMenu={(e) => {
                      e.preventDefault();
                      // Per Tastatur (Kontextmenü-Taste, Umschalt+F10) gibt es
                      // keine Zeigerposition — dann an der Zeile öffnen.
                      if (e.clientX === 0 && e.clientY === 0) {
                        const r = e.currentTarget.getBoundingClientRect();
                        setMenue({ x: r.left + 24, y: r.top + 24, id: m.id });
                      } else {
                        setMenue({ x: e.clientX, y: e.clientY, id: m.id });
                      }
                    }}
                    aria-current={m.id === selectedId ? "true" : undefined}
                    data-unread={m.seen === 0}
                    data-priority={m.priority ?? ""}
                    data-spam={spam}
                    style={{ ["--rail-color" as string]: categoryColor(m.category) }}
                    onClick={() => (auswahlModus ? toggle(m.id, false) : onSelect(m.id))}
                  >
                    <Avatar email={m.from_email} name={absenderName} className="msg-avatar">
                      {picked.has(m.id) ? <CheckCheck /> : undefined}
                    </Avatar>
                    <div className="msg-inhalt">
                    <div className="msg-row">
                      {/* Ungelesen auf einen Blick — Fettschrift allein übersieht man. */}
                      <span className="unread-dot" aria-hidden="true" />
                      {m.seen === 0 && <span className="sr-only">Ungelesen: </span>}
                      <span className="msg-from" title={m.from_email}>{absenderName}</span>
                      {(m.thread_anzahl ?? 0) > 1 && (
                        <span className="thread-zahl" title={`${m.thread_anzahl} Nachrichten in dieser Unterhaltung`}>{m.thread_anzahl}</span>
                      )}
                      {m.attachment_count > 0 && (
                        <Paperclip className="msg-icon" aria-label={`${m.attachment_count} ${m.attachment_count === 1 ? "Anhang" : "Anhänge"}`} />
                      )}
                      {m.flagged === 1 && <Flag className="msg-icon" data-flag="true" aria-label="markiert" />}
                      <time className="msg-date" dateTime={m.date} title={datumLang(m.date)}>{datumKurz(m.date)}</time>
                    </div>
                    <div className="msg-subject" title={m.subject || undefined}>
                      {unterordnerVon(m.folder_path, m.folder_id, ordner) && (
                        <span className="msg-ordner" title={`Einsortiert in ${m.folder_path}`}>{unterordnerVon(m.folder_path, m.folder_id, ordner)}</span>
                      )}
                      {m.subject || "(kein Betreff)"}
                    </div>
                    {(m.summary || m.snippet) && <div className="msg-snippet">{m.summary || m.snippet}</div>}

                    {/* Bewusst Boolean(): eine ||-Kette gibt bei lauter falschen
                        Werten den letzten zurück — bei 0 rendert React sichtbar "0". */}
                    {Boolean((m.category && m.category !== "spam") || m.pending_actions > 0 || m.draft_id || (m.spam_score ?? 0) >= 0.5) && (
                      <div className="msg-meta">
                        {(m.spam_score ?? 0) >= 0.5 && (
                          <span className="chip warn">Spam-Verdacht {Math.round((m.spam_score ?? 0) * 100)} %</span>
                        )}
                        {m.category && m.category !== "spam" && (
                          <span className="chip" style={{ ["--chip-color" as string]: categoryColor(m.category) }}>
                            {m.category}
                          </span>
                        )}
                        {m.pending_actions > 0 && (
                          <span className="chip" style={{ ["--chip-color" as string]: "var(--accent)" }}>Vorschlag</span>
                        )}
                        {Boolean(m.draft_id) && (
                          <span className="chip" style={{ ["--chip-color" as string]: "var(--ok)" }}>Entwurf</span>
                        )}
                      </div>
                    )}
                    </div>
                  </button>
                  </WischZeile>
                </li>
              );
            })}
          </ul>
        )}
        {messages.length > 0 && mehrDa && onMehr && (
          <NachladeMarke loading={loading} onMehr={onMehr} />
        )}
        {messages.length > 0 && !mehrDa && messages.length > 20 && (
          <p className="listen-ende">Ende der Liste</p>
        )}
      </div>

      {menue && (
        <KontextMenue
          x={menue.x}
          y={menue.y}
          eintraege={menueFuer(menue.id)}
          onClose={() => setMenue(null)}
          onUnterwahl={(eintrag, wert) => {
            if (eintrag !== "verschieben") return;
            void ausfuehren(zielIds(menue.id), "move", wert);
          }}
        />
      )}
    </section>
  );
}

/** Platzhalterzeilen, solange die erste Seite lädt — die Liste springt dann nicht. */
function ListenSkelett() {
  return (
    <ul className="msg-list skelett" aria-label="Nachrichten werden geladen" aria-busy="true">
      {Array.from({ length: 8 }, (_, i) => (
        <li key={i} className="skelett-zeile">
          <span style={{ width: `${40 + ((i * 17) % 35)}%` }} />
          <span style={{ width: `${60 + ((i * 23) % 30)}%` }} />
          <span style={{ width: `${45 + ((i * 13) % 40)}%` }} />
        </li>
      ))}
    </ul>
  );
}

/**
 * Lädt ältere Mails, sobald das Ende der Liste in Sicht kommt — und als
 * Knopf für alle, die lieber klicken oder deren Browser nicht beobachtet.
 */
function NachladeMarke({ loading, onMehr }: { loading: boolean; onMehr: () => void }) {
  const marke = useRef<HTMLDivElement | null>(null);
  const mehr = useRef(onMehr);
  mehr.current = onMehr;
  useEffect(() => {
    const el = marke.current;
    if (!el || loading) return;
    const beobachter = new IntersectionObserver((e) => { if (e[0]?.isIntersecting) mehr.current(); }, { rootMargin: "300px" });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [loading]);
  return (
    <div ref={marke} className="nachladen">
      <button type="button" className="btn quiet" onClick={onMehr} disabled={loading}>{loading ? "Lade …" : "Ältere laden"}</button>
    </div>
  );
}

/**
 * Name des Unterordners, wenn die Mail nicht direkt im gezeigten Ordner liegt:
 * im Posteingang „Kunden / System4", in Ansichten wie „Ungelesen" alles außer
 * dem Posteingang selbst. Sonst null — dann braucht es keine Markierung.
 */
function unterordnerVon(pfad: string | undefined, folderId: number, ordner: Folder | undefined): string | null {
  if (!pfad) return null;
  if (ordner) {
    if (folderId === ordner.id) return null;
    const rest = pfad.startsWith(`${ordner.path}.`) || pfad.startsWith(`${ordner.path}/`) ? pfad.slice(ordner.path.length + 1) : pfad;
    return rest.split(/[./]/).join(" / ");
  }
  if (/^INBOX$/i.test(pfad)) return null;
  return pfad.replace(/^INBOX[./]/i, "").split(/[./]/).join(" / ");
}
